import * as Accountant from 'app/calcs/Accountant'
import { Local145PensionCalculations } from 'app/calcs/customers/local145'
import { ILocal145PensionInputs, Local145FormOfPayment } from 'app/calcs/customers/local145/interfaces'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { NumberSelector } from 'app/components/form/NumberSelector';
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import { Infobox } from 'app/components/Infobox';
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { cachedLocal145ExportExhibitTab } from 'app/redux/Customers/local145/actions'
import { dateAtAge, IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo'
import { WorkbookServiceResponse } from 'app/services/models/Workbook'
import * as utils from 'app/utils'
import { RequestCache } from 'app/utils/RequestCache'
import * as Config from 'config'
import { getBrandHelpers } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'

const debug = utils.makeDebug(true)

enum ExhibitGenerationState {
  idle, generating, ready
}

interface ILocal145PensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  monthlyPayout: number
  totalLumpSum: number
  exhibitGeneration: ExhibitGenerationState
  downloadFilename: string

  // these are synced with profile.pension.local145
  beginPaymentsAge: moment.Duration
  formOfPayment: Local145FormOfPayment
  percentLumpSumElected: number
}

class Local145PensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, ILocal145PensionContainerState> {
  beginPaymentsAgeNS = 'pension.local145.beginPaymentsAge'
  hoursWorkedNS = 'pension.shared.hoursWorked'
  formOfPaymentNS = 'pension.local145.formOfPayment'
  percentLumpSumElectedNS = 'pension.local145.percentLumpSumElected'

  state: ILocal145PensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    totalLumpSum: 0,
    monthlyPayout: 0,
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null,
    percentLumpSumElected: this.getValueForQuestion(this.percentLumpSumElectedNS) || 0,
    exhibitGeneration: ExhibitGenerationState.idle,
    downloadFilename: null
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    // tslint:disable-next-line:no-console
    //console.log('local145.debounceCalculations entered')
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const inputs = this.state.profile.pension.local145
    const { beginPaymentsAge } = this.state;
    const when = dateAtAge(person, beginPaymentsAge)

    const computer = new Local145PensionCalculations(this.state.profile)
    computer.setSpouse(profile.getSpouse())
    computer.getMonthlyPensionAmount(person, inputs, when)
      .then((payout) => {
        const { amount, totalLumpSum, totalPensionCredits, totalVestingService } = payout
        this.setState({ monthlyPayout: amount, totalLumpSum })
      })

  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()

      if (profile.pension.local145) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: ILocal145PensionContainerState) {
    this.updateCalculations(nextState)
  }

  private updateCalculations(nextState: ILocal145PensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, beginPaymentsAge } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const updates = {
      beginPaymentsAge,
      formOfPayment
    }
    const newLocal145 = _.defaults({}, updates, profile.pension.local145)

    if (!this.initCalc || !_.isEqual(profile.pension.local145, newLocal145)) {
      this.initCalc = true
      profile.pension.local145 = newLocal145
      this.debounceCalculations()
    }
  }

  private updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.local145.beginPaymentsAge = beginPaymentsAge
    const exhibitGeneration = ExhibitGenerationState.idle
    this.setState({ beginPaymentsAge, exhibitGeneration })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {
    const profile = this.state.profile
    const local145 = profile.pension.local145
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.local145 || !this.state.beginPaymentsAge) { return null }

    return (
      <section className="animated fadeIn">
        {this.renderIntro()}
        {('N' === local145.statusCode)? this.renderNotActive() : this.renderFullInfo()}
      </section>)
  }

  renderIntro() {
    const aStyle = {
      marginBottom: '10px'
    }
    const censusDateText = theCensusInfo.censusDate.format('LL')

    return <div>
      <h1>Local 145 Pension</h1>
      <h5 className="lead" style={aStyle}>
      We have the following information from your records.  Total credits shown do not include
      any additional tenths, which are only computed at the time of retirement.  This information
      is current as of {censusDateText}. {theCensusInfo.publicNotes}
      </h5>
    </div>
  }

  renderFullInfo() {

    const profile = this.state.profile
    const local145 = profile.pension.local145
    const spouseIsValid = profile.spouseIsValid()
    const showSummary = this.state.monthlyPayout !== 0
    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter(getBrandHelpers().memberPensionFormsOfPayment, (e) => findFop(e))

    return (
      <div>
        <Row>
          <Col md={12}>
            <ul >
              <li>Inside Credits: <a>{local145.insideCredits}</a></li>
              <li>Residential Credits: <a>{local145.residentialCredits}</a></li>
              <li>Teledata Credits: <a>{local145.teledataCredits}</a></li>
            </ul>
            <div className='lead'>
              The Local 145 Pension calculation below projects future credits that will continue
              to be earned (one per year) up to your selected age of retirement.  Any change
              will effect your monthly benefit.</div>
          </Col>
        </Row>
        <Row>
          <Col md={6}>

            <FormGroup>
              <RetirementAgeSelector min={55} max={70}
                placeholder="What age will you begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup>
              <FormOfPayment name={this.formOfPaymentNS}
                defaultValue={this.state.formOfPayment}
                options={allowedFormsOfPayment}
                onChange={ formOfPayment => this.setStateAndSave({ formOfPayment }) } />
            </FormGroup>

          </Col>
          <Col md={6}>
            {showSummary && <h2 className="animated fadeIn">
              {this.renderPayoutText()}
            </h2>}

          </Col>
        </Row>
      </div >
    )
  }

  renderPayoutText() {
    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const monthlyPayoutText = utils.toCurrency(this.state.monthlyPayout)
    const a = <span>If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
    <a>{beginPaymentsAgeText}</a>, </span>
    const b = (this.state.formOfPayment === Local145FormOfPayment.partialLumpSum)?
      <span>the lump sum will be <a>{utils.toCurrency(this.state.totalLumpSum)}</a> and </span>
      : ''
    const c = <span>the monthly amount will be <a>{monthlyPayoutText}</a>.</span>
    return <span>{a}{b}{c}</span>
  }

  renderNotActive() {
    const aStyle = {
      color: 'red'
    }
    return <div>
      <h5 className="lead" style={aStyle}>Ah, it appears that you are not eligible for the Plan.</h5>
    </div>
  }
}

const Local145PensionQuestionsGroup = () => (
  <Local145PensionContainer
    header="Local 145 Pension" icon="" />
)

export {
  Local145PensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/pension/Local145/index.tsx